import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import HeaderNoUnderline from '../../../frame/components/header/HeaderNoUnderline';
import TabPanels from '../tabs/TabPanels';
import { ReactComponent as TracksIcon } from '../../../assets/icons/ic_action_polygom_dark.svg';
import styles from './trackcontextmenu.module.css';
import AppContext, {
    isCloudTrack,
    isLocalTrack,
    isRouteTrack,
    isShareTrack,
    isTravelTrack,
    OBJECT_TYPE_CLOUD_TRACK,
} from '../../../context/AppContext';
import MenuItemWithLines from '../../../menu/components/MenuItemWithLines';
import { getFileName } from '../../../manager/track/TracksManager';
import ThreeDotsButton from '../../../frame/components/btns/ThreeDotsButton';
import ActionsMenu from '../../../menu/actions/ActionsMenu';
import TrackActions from '../../../menu/actions/TrackActions';
import { useTrackVisibility } from '../../../util/hooks/menu/useTrackVisibility';
import { useCompactOnScroll } from '../../../util/hooks/useCompactOnScroll';
import CloudTrackActionsButtons from './CloudTrackActionsButtons';
import RouteTrackActionsButtons from './RouteTrackActionsButtons';
import LocalTrackActionsButtons from './LocalTrackActionsButtons';
import ShareTrackActionsButtons from './ShareTrackActionsButtons';
import TravelTrackActionsButtons from './TravelTrackActionsButtons';
import TravelFocusToggleBtn from './TravelFocusToggleBtn';
import LocationInfoLine from '../common/LocationInfoLine';
import { useFocusMode } from '../../../util/hooks/map/useFocusMode';

export default function TrackContextMenu({ track, onClose, tabsObj, showBackButton = false }) {
    const ctx = useContext(AppContext);
    const { setSelectionFocus, clearSelectionFocus } = useFocusMode();

    const anchorEl = useRef(null);
    const collapsibleHeaderRef = useRef(null);
    const [openActions, setOpenActions] = useState(false);
    const [collapseReserve, setCollapseReserve] = useState(0);
    const { compact, scrollAreaHandlers } = useCompactOnScroll();

    const { toggleVisibility, checkedSwitch } = useTrackVisibility({ file: track });

    useEffect(() => {
        if (track?.name && isCloudTrack(ctx)) {
            setSelectionFocus({ type: OBJECT_TYPE_CLOUD_TRACK, id: track.name });
        }

        return () => clearSelectionFocus();
    }, [track?.name, ctx.currentObjectType]);

    useLayoutEffect(() => {
        if (collapsibleHeaderRef.current) {
            const height = collapsibleHeaderRef.current.scrollHeight;
            setCollapseReserve((prev) => (prev !== height ? height : prev));
        }
    }, [compact, track, ctx.currentObjectType]);

    const trackName = track ? getFileName(track) : null;
    const showActionsBtn = (isCloudTrack(ctx) || isLocalTrack(ctx) || isTravelTrack(ctx)) && track?.name;
    const compactToolbarProps = compact ? { className: styles.compactHeaderToolbar } : undefined;

    if (!tabsObj || tabsObj.tabList.length === 0) {
        return null;
    }

    return (
        <Box id="se-track-context-menu" className={styles.trackContextMenuShell}>
            <HeaderNoUnderline
                title={compact ? trackName : ''}
                onClose={onClose}
                showBackButton={showBackButton}
                toolbarProps={compactToolbarProps}
                rightContent={
                    <>
                        {isTravelTrack(ctx) && track && <TravelFocusToggleBtn />}
                        {showActionsBtn && (
                            <ThreeDotsButton
                                name={'action_menu_track'}
                                tip={'shared_string_menu'}
                                id={`se-actions-${track.name}`}
                                setOpenActions={setOpenActions}
                                anchorEl={anchorEl}
                            />
                        )}
                    </>
                }
            />
            <Box
                ref={collapsibleHeaderRef}
                className={`${styles.collapsibleHeader} ${compact ? styles.collapsibleHeaderHidden : ''}`}
            >
                {trackName && (
                    <Box className={styles.nameBlock}>
                        <MenuItemWithLines
                            id={'se-track-' + trackName}
                            className={styles.trackName}
                            name={trackName}
                            maxLines={3}
                        />
                        <Box className={styles.trackIconBox}>
                            <TracksIcon />
                        </Box>
                    </Box>
                )}
                {track && <LocationInfoLine track={track} />}
                {isCloudTrack(ctx) && track && (
                    <CloudTrackActionsButtons
                        track={track}
                        toggleVisibility={toggleVisibility}
                        checkedSwitch={checkedSwitch}
                    />
                )}
                {isLocalTrack(ctx) && track && <LocalTrackActionsButtons track={track} />}
                {isRouteTrack(ctx) && track && <RouteTrackActionsButtons track={track} />}
                {isShareTrack(ctx) && track && <ShareTrackActionsButtons track={track} />}
                {isTravelTrack(ctx) && track && <TravelTrackActionsButtons track={track} />}
            </Box>
            <Box className={styles.trackTabsColumn}>
                <TabPanels
                    tabsObj={tabsObj}
                    scrollAreaHandlers={scrollAreaHandlers}
                    scrollPaddingBottom={compact ? collapseReserve : 0}
                />
            </Box>
            <ActionsMenu
                open={openActions}
                setOpen={setOpenActions}
                anchorEl={anchorEl}
                actions={
                    track && (
                        <TrackActions
                            track={track}
                            setDisplayTrack={toggleVisibility}
                            setOpenActions={setOpenActions}
                        />
                    )
                }
            />
        </Box>
    );
}
